'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Sensodyne Have Microplastics? All 14 Formulas Checked (2026)"
      author="MicroPlastics Research Desk"
      date="August 1, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Does Sensodyne have microplastics: every Sensodyne and Pronamel formula audited against the ingredient labels Haleon files with the FDA"
    >
      <BlogLastUpdated iso="2026-08-01" formatted="August 1, 2026" />

      <BlogTLDR>
        <strong>No plastic beads, but yes to synthetic polymers in most
        formulas.</strong>{' '}
        The scrubbing microbeads people remember are genuinely gone, US
        manufacturing ended in 2018 under the Microbead-Free Waters Act. What
        remains is different: we read all <strong>14 Sensodyne and Pronamel
        ingredient labels that Haleon files with the FDA</strong>, and{' '}
        <strong>11 of them list PEG-8</strong>, a petroleum-derived synthetic
        polymer. Four of those also list <strong>PVM/MA copolymer</strong>, and
        two list <strong>polyacrylic acid</strong>. These dissolve rather than
        shedding solid particles, so whether they count as
        &ldquo;microplastics&rdquo; is a real definitional fight, not a settled
        fact. Two formulas contain none of them:{' '}
        <strong>Sensodyne Cavity &amp; Sensitivity</strong> and{' '}
        <strong>Pronamel Gentle Whitening Advanced Plus</strong>. If you want to
        stay with Sensodyne, those are the two to buy.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>We checked <strong>Haleon&apos;s own FDA-filed labels</strong> for all 14 Sensodyne and Pronamel products, not a third-party ingredient database.</>,
          <><strong>PEG-8 appears in 11 of 14 formulas</strong>, including every Pronamel Clinical, both Clinical White formulas, and all three Pronamel Kids flavours.</>,
          <><strong>Two formulas are clean</strong> of PEG, PVM/MA and polyacrylic acid: Sensodyne Cavity &amp; Sensitivity and Pronamel Gentle Whitening Advanced Plus.</>,
          <>Solid plastic <strong>microbeads are not in any of them</strong>. US manufacturing ended in 2018; anyone claiming Sensodyne still contains scrubbing beads is years out of date.</>,
          <>The polymers that remain are <strong>water-soluble</strong>, so they are not microplastic particles under a strict particle definition, though campaigners count them as plastic ingredients. We explain the disagreement rather than picking a side for you.</>,
          <><strong>Xanthan gum, cellulose gum and carrageenan are not plastics.</strong> They are natural polysaccharides, and lists that flag them are wrong.</>,
        ]}
      />

      <InlineGuessReveal
        source="sensodyne_peg_count"
        question="Sensodyne files 14 formulas with the FDA. How many list PEG-8, a petroleum-derived polymer?"
        options={[
          { label: 'None' },
          { label: '3 of 14' },
          { label: '11 of 14', correct: true },
          { label: 'All 14' },
        ]}
        answer="11 of 14"
        answerNote={
          <>
            PEG-8 is Sensodyne&apos;s house humectant. Only two formulas are
            free of it and of PVM/MA and polyacrylic acid: Cavity &amp;
            Sensitivity, and Pronamel Gentle Whitening Advanced Plus. So the
            swap here does not mean leaving the brand.
          </>
        }
        ctaText="The tube says nothing about this. Scan it and see which of the 14 you own."
        ctaLabel="Scan your toothpaste"
      />

      <RiskQuizInlineNudge source="blog_inline_sensodyne" />

      <KeyStats
        heading="Sensodyne and synthetic polymers: what the labels actually say"
        stats={[
          { value: '11 of 14', label: 'formulas listing PEG-8', context: 'a petroleum-derived synthetic polymer used as a humectant', source: 'Haleon FDA labels via DailyMed', sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
          { value: '2', label: 'formulas with no synthetic polymer', context: 'Cavity & Sensitivity and Pronamel Gentle Whitening Advanced Plus', source: 'Haleon FDA labels via DailyMed', sourceUrl: 'https://dailymed.nlm.nih.gov/dailymed/' },
          { value: '2018', label: 'US microbead manufacturing ban took effect', context: 'solid plastic scrubbing beads are gone from all US toothpaste', source: 'Microbead-Free Waters Act of 2015', sourceUrl: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs' },
          { value: 'Soluble', label: 'not solid particles', context: 'PEG and PVM/MA dissolve, so they are not microplastics under a strict definition', source: 'ingredient chemistry', sourceUrl: 'https://www.beatthemicrobead.org/myth-buster-toothpaste-still-contains-plastic-ingredients/' },
        ]}
      />

      <h2>The short answer, and the part everyone gets wrong</h2>
      <p>
        Two different things get called &ldquo;microplastics in toothpaste,&rdquo;
        and conflating them is why the internet is so confused about this.
      </p>
      <p>
        The first is <strong>solid polyethylene microbeads</strong>, the little
        coloured specks that used to be added as scrubbers and famously lodged
        in people&apos;s gums. Those are genuinely, completely gone. The
        Microbead-Free Waters Act banned their manufacture in US rinse-off
        cosmetics, with toothpaste manufacturing ending in <strong>2018</strong>.
        If a page tells you Sensodyne still contains scrubbing beads, it is
        recycling a decade-old story.
      </p>
      <p>
        The second is <strong>soluble synthetic polymers</strong>, and this is
        the live question. These are petroleum-derived plastics in the chemical
        sense, but they are formulated to dissolve in water rather than exist as
        discrete particles. They do the boring jobs: holding moisture, thickening
        the paste, keeping the formula stable. This is where Sensodyne actually
        sits, so it is what we went and checked.
      </p>

      <h2>How we checked: Haleon&apos;s own filings</h2>
      <p>
        Toothpaste containing fluoride is regulated in the US as an
        over-the-counter drug, which means the manufacturer has to file a full
        ingredient label with the FDA. That is a far better source than a
        third-party ingredient database, which is typically user-uploaded and
        often years stale.
      </p>
      <p>
        So we pulled <strong>all 14 Sensodyne and Pronamel labels filed by
        Haleon US Holdings LLC</strong> in the FDA&apos;s DailyMed system and
        read the inactive-ingredient lists directly. Everything below is what
        Haleon itself states. Where a formula is clean, we say so; where it
        isn&apos;t, the ingredient is named.
      </p>

      <h2>Every Sensodyne formula, by synthetic polymer content</h2>

      <BlogComparisonTable
        caption="Sensodyne and Pronamel formulas, from Haleon's FDA-filed inactive ingredient lists (checked August 2026)"
        columns={[
          { key: 'product', label: 'Formula' },
          { key: 'polymers', label: 'Synthetic polymers listed' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { product: 'Sensodyne Cavity & Sensitivity', polymers: 'None', verdict: 'Cleanest option' },
          { product: 'Pronamel Gentle Whitening Advanced Plus', polymers: 'None', verdict: 'Cleanest option' },
          { product: 'Pronamel Clinical Enamel Strength (Fresh Breath, Whitening Action, Deep Clean)', polymers: 'PEG-8, PVM/MA copolymer', verdict: 'Two polymers' },
          { product: 'Pronamel Intensive Enamel Repair Clean Mint', polymers: 'PEG-8, PVM/MA copolymer', verdict: 'Two polymers' },
          { product: 'Sensodyne Clinical White (Intensive Clean, Stain Protector)', polymers: 'PEG-8', verdict: 'One polymer' },
          { product: 'Sensodyne Whiten & Shine', polymers: 'PEG-8, polyacrylic acid', verdict: 'Two polymers' },
          { product: 'Sensodyne Complete Protection (Mint, Extra Fresh)', polymers: 'PEG-8, polyacrylic acid', verdict: 'Two polymers' },
          { product: 'Pronamel Kids (Bubble Mint, Berry Twist, Bubble Twist)', polymers: 'PEG-8', verdict: 'One polymer' },
        ]}
      />

      <p>
        The pattern is consistent: <strong>PEG-8 is Sensodyne&apos;s house
        humectant</strong>. It shows up across the Pronamel Clinical line, both
        Clinical White formulas, Complete Protection, Whiten &amp; Shine and
        every Pronamel Kids flavour. The two exceptions share a noticeably
        shorter list, water, sorbitol, hydrated silica, glycerin, cocamidopropyl
        betaine, flavour, xanthan gum, titanium dioxide, sodium saccharin and
        sodium hydroxide, with no synthetic polymer at all.
      </p>

      <h2>What these ingredients actually are</h2>
      <ul>
        <li>
          <strong>PEG-8 (polyethylene glycol).</strong> A petroleum-derived
          synthetic polymer built from repeating ethylene oxide units. Used to
          hold moisture and keep the paste from drying out. Water-soluble, so it
          does not persist as a particle.
        </li>
        <li>
          <strong>PVM/MA copolymer.</strong> Polyvinyl methyl ether / maleic
          anhydride, a synthetic copolymer that helps active ingredients stick to
          the tooth surface. It is doing real clinical work in the Pronamel
          formulas, which is worth weighing against wanting it out.
        </li>
        <li>
          <strong>Polyacrylic acid.</strong> A synthetic polymer used as a
          thickener and to control mineral deposits.
        </li>
        <li>
          <strong>Not plastics, despite what some lists say:</strong> xanthan gum
          (fermented sugar), cellulose gum (plant cellulose) and carrageenan
          (seaweed). These are natural polysaccharides. Any &ldquo;avoid
          these&rdquo; list that includes them is padding its numbers.
        </li>
      </ul>

      <h2>So do they count as microplastics?</h2>
      <p>
        Honestly: it depends whose definition you use, and we are not going to
        pretend otherwise.
      </p>
      <p>
        Under the definition most scientists use, a microplastic is a{' '}
        <strong>solid, insoluble plastic particle</strong> under 5&nbsp;mm. PEG-8
        and PVM/MA dissolve, so on that definition they are not microplastics and
        Sensodyne contains none. Under the broader definition campaign groups
        like Beat the Microbead use, any synthetic petroleum-derived polymer in a
        personal-care product counts as a <em>plastic ingredient</em>, soluble or
        not, because it still enters wastewater and still comes from fossil
        feedstock. On that definition, 11 of 14 Sensodyne formulas qualify.
      </p>
      <p>
        Both positions are defensible. What is <em>not</em> defensible is the
        common claim that these polymers shed measurable microplastic particles
        into your mouth while you brush. No published study has demonstrated
        that, and we are not aware of any lab that has measured particle release
        from a Sensodyne formula specifically. Any exact particle count you see
        attributed to Sensodyne is invented.
      </p>

      <h2>What to use instead</h2>
      <ul>
        <li>
          <strong>Stay with Sensodyne, switch the formula.</strong> If you use
          Sensodyne for genuine tooth sensitivity, the easiest change is not
          leaving the brand, it is picking{' '}
          <strong>Cavity &amp; Sensitivity</strong> or{' '}
          <strong>Pronamel Gentle Whitening Advanced Plus</strong>, which carry
          the same potassium-nitrate or fluoride actives without the polymers.
        </li>
        <li>
          <strong>Don&apos;t drop a working treatment over this.</strong> The
          polymers here are soluble and unmeasured. Untreated sensitivity and
          untreated decay are concrete problems with concrete consequences. This
          is a swap-if-convenient issue, not a stop-using-toothpaste issue.
        </li>
        <li>
          <strong>If you want fully polymer-free, go outside the brand.</strong>{' '}
          See our{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">microplastic-free toothpaste picks</Link>{' '}
          for hydroxyapatite and tooth-tab options, and{' '}
          <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">microplastics in toothpaste</Link>{' '}
          for the category-wide explainer.
        </li>
        <li>
          <strong>Check the floss too.</strong> The bigger plastics question in
          most bathrooms is{' '}
          <Link href="/blog/pfas-in-dental-floss" className="text-forest underline">PFAS in dental floss</Link>, where the evidence is stronger than it is here.
        </li>
      </ul>

      <BlogAppCTA
        headline="Scan the tube in your bathroom"
        sub="Point the MicroPlastics app at your Sensodyne, Crest or any toothpaste to see which synthetic polymers are on the label and what a cleaner swap looks like."
      />

      <p>
        A second dataset: the{' '}
        <Link href="/brands/sensodyne" className="text-forest underline">
          Sensodyne brand page
        </Link>{' '}
        shows what Open Beauty Facts lists on Sensodyne ingredient labels, a
        crowdsourced and Europe-weighted sample.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Does Sensodyne have microplastics?',
            answer: 'It has no solid plastic microbeads, those were banned and US manufacturing ended in 2018. It does contain synthetic polymers: 11 of the 14 Sensodyne and Pronamel formulas Haleon files with the FDA list PEG-8, four also list PVM/MA copolymer, and two list polyacrylic acid. These are water-soluble, so they are not microplastic particles under a strict scientific definition, though campaign groups count them as plastic ingredients.',
          },
          {
            question: 'Which Sensodyne has no synthetic polymers?',
            answer: 'Two formulas: Sensodyne Cavity & Sensitivity and Pronamel Gentle Whitening Advanced Plus. Both list only water, sorbitol, hydrated silica, glycerin, cocamidopropyl betaine, flavour, xanthan gum, titanium dioxide, sodium saccharin and sodium hydroxide, with no PEG, PVM/MA or polyacrylic acid. Xanthan gum is a natural polysaccharide, not a plastic.',
          },
          {
            question: 'Is PEG-8 in toothpaste dangerous?',
            answer: 'PEG-8 is a petroleum-derived synthetic polymer used to keep toothpaste from drying out, and it is water-soluble rather than a solid particle. There is no evidence it releases microplastic particles into the mouth, and no lab has published a particle count for a Sensodyne formula. The objection to it is mainly that it is a fossil-derived polymer entering wastewater, not a demonstrated health risk at these levels.',
          },
          {
            question: 'Does Sensodyne still have microbeads?',
            answer: 'No. The Microbead-Free Waters Act banned manufacturing of plastic microbeads in US rinse-off cosmetics, with toothpaste manufacturing ending in 2018. No current Sensodyne formula contains them. Claims that it does are recycling pre-2018 coverage.',
          },
          {
            question: 'Is Sensodyne or Crest worse for synthetic polymers?',
            answer: 'They use different ones. Sensodyne leans on PEG-8, which appears in 11 of its 14 FDA-filed formulas. Crest does not use PEG in its filed toothpaste labels, but the Crest Plus Scope whitening line uses poloxamer 407, and Crest Strawberry (a kids formula) uses carbomer. Neither brand contains solid microbeads.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Haleon US Holdings LLC', year: 2026, title: 'Sensodyne and Pronamel structured product labels (all 14 US formulas, inactive ingredient listings)', publisher: 'FDA DailyMed', url: 'https://dailymed.nlm.nih.gov/dailymed/search.cfm?labeltype=all&query=sensodyne' },
          { authors: 'US Food and Drug Administration', year: 2015, title: 'The Microbead-Free Waters Act: FAQs (manufacturing ban dates for rinse-off cosmetics and toothpaste)', publisher: 'FDA', url: 'https://www.fda.gov/cosmetics/cosmetics-laws-regulations/microbead-free-waters-act-faqs' },
          { authors: 'Plastic Soup Foundation', year: 2026, title: 'Myth buster: toothpaste still contains plastic ingredients', publisher: 'Beat the Microbead', url: 'https://www.beatthemicrobead.org/myth-buster-toothpaste-still-contains-plastic-ingredients/' },
        ]}
      />
    </BlogPostLayout>
  )
}
